/**
 * High-level Recorder — wraps the AudioWorklet, AudioContext, and getUserMedia
 * setup. Buffers Float32 frames in memory while recording; on stop, returns the
 * concatenated PCM together with duration and peak amplitude.
 *
 * Float32 → 24-bit PCM int conversion lives here too (used by the upload path).
 */

import type { RecorderFrameMessage } from '../worklets/recorder.worklet'

export interface RecorderOptions {
  /** Sample rate in Hz. The OHEAR studio standard is 48000. */
  sampleRate: number
  /** Bit depth of the eventual upload payload. Currently only 24 is supported. */
  bitDepth: 24
  /**
   * If true, the reference voice (any other audio playing in the page) should
   * be muted while recording. The Recorder itself doesn't own playback — this
   * flag is forwarded to UI code via {@link Recorder.options}.
   */
  refVoiceMuteOnRecord: boolean
  /**
   * URL of the compiled worklet JS module. Bundlers emit this; see the build
   * note in `worklets/recorder.worklet.ts`. Required at runtime.
   */
  workletUrl: string | URL
}

export interface RecorderResult {
  /** Concatenated mono Float32 PCM in [-1, 1]. */
  pcm: Float32Array
  /** Duration of the captured audio in seconds (samples / sampleRate). */
  durationSec: number
  /** Maximum |sample| observed during the take, [0, 1]. */
  peak: number
}

type RecorderState = 'idle' | 'recording' | 'stopped'

export class Recorder {
  private audioContext?: AudioContext
  private workletNode?: AudioWorkletNode
  private mediaStream?: MediaStream
  private sourceNode?: MediaStreamAudioSourceNode
  private chunks: Float32Array[] = []
  private peak = 0
  private state: RecorderState = 'idle'

  constructor(public readonly options: RecorderOptions) {
    if (options.bitDepth !== 24) {
      throw new Error(`Recorder: bitDepth ${String(options.bitDepth)} not supported (24 only)`)
    }
  }

  /** Begin capture. Throws if already recording or if the mic is denied. */
  async start(): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error(`Recorder.start: invalid state ${this.state}`)
    }

    this.audioContext = new AudioContext({ sampleRate: this.options.sampleRate })

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: this.options.sampleRate,
        channelCount: 1,
        // Disable browser DSP — we want raw mic for VO.
        echoCancellation: false,
        autoGainControl: false,
        noiseSuppression: false,
      },
    })

    await this.audioContext.audioWorklet.addModule(this.options.workletUrl.toString())

    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.workletNode = new AudioWorkletNode(this.audioContext, 'ohear-recorder')
    this.workletNode.port.onmessage = (e: MessageEvent<RecorderFrameMessage>) => {
      this.onFrames(e.data)
    }
    this.sourceNode.connect(this.workletNode)
    // We deliberately do NOT connect the worklet to destination — we don't
    // want monitoring through the browser (it'd cause feedback in headphones).

    this.state = 'recording'
  }

  /**
   * Stop capture and return the concatenated PCM. Releases the mic and
   * AudioContext.
   */
  async stop(): Promise<RecorderResult> {
    if (this.state !== 'recording') {
      throw new Error(`Recorder.stop: invalid state ${this.state}`)
    }
    this.state = 'stopped'
    await this.teardown()

    const pcm = concatFloat32(this.chunks)
    const durationSec = pcm.length / this.options.sampleRate
    return { pcm, durationSec, peak: this.peak }
  }

  /** Abort recording and discard all buffered audio. Idempotent. */
  cancel(): void {
    if (this.state === 'idle') return
    this.state = 'idle'
    this.chunks = []
    this.peak = 0
    void this.teardown()
  }

  /** Live peak observed since start(). Cleared on cancel(). */
  get currentPeak(): number {
    return this.peak
  }

  private onFrames(msg: RecorderFrameMessage): void {
    if (msg.type !== 'frames') return
    if (this.state !== 'recording') return
    this.chunks.push(msg.frames)
    if (msg.peak > this.peak) this.peak = msg.peak
  }

  private async teardown(): Promise<void> {
    try {
      this.sourceNode?.disconnect()
      this.workletNode?.disconnect()
      if (this.workletNode) this.workletNode.port.onmessage = null
      this.mediaStream?.getTracks().forEach((t) => t.stop())
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close()
      }
    } finally {
      this.sourceNode = undefined
      this.workletNode = undefined
      this.mediaStream = undefined
      this.audioContext = undefined
    }
  }
}

/** Concatenates a list of Float32 buffers into one. */
export function concatFloat32(chunks: Float32Array[]): Float32Array {
  let total = 0
  for (const c of chunks) total += c.length
  const out = new Float32Array(total)
  let off = 0
  for (const c of chunks) {
    out.set(c, off)
    off += c.length
  }
  return out
}

/**
 * Convert Float32 PCM in [-1, 1] to 24-bit signed little-endian PCM.
 * Each sample becomes 3 bytes. Out-of-range samples are clamped.
 *
 * Note the asymmetry: positive full-scale uses 0x7FFFFF (8 388 607) and
 * negative full-scale uses 0x800000 (8 388 608). This matches the int24 range
 * [-8388608, +8388607] exactly.
 */
export function float32ToInt24(input: Float32Array): Uint8Array {
  const out = new Uint8Array(input.length * 3)
  for (let i = 0; i < input.length; i++) {
    const raw = input[i] ?? 0
    const s = raw < -1 ? -1 : raw > 1 ? 1 : raw
    const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff)
    const o = i * 3
    out[o] = v & 0xff
    out[o + 1] = (v >> 8) & 0xff
    out[o + 2] = (v >> 16) & 0xff
  }
  return out
}
